import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const EVENT_LABELS = {
  critical_finding: 'Critical Finding',
  scan_complete: 'Scan Complete',
  scan_failed: 'Scan Failed',
  scan_paused: 'Scan Paused',
  engagement_status_change: 'Engagement Update',
  report_generated: 'Report Generated'
};

const EVENT_COLORS = {
  critical_finding: '#ef4444',
  scan_complete: '#22c55e',
  scan_failed: '#ef4444',
  scan_paused: '#f59e0b',
  engagement_status_change: '#3b82f6',
  report_generated: '#8b5cf6'
};

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const seconds = Math.floor((now - date) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

function getEntityPath(entityType, entityId) {
  if (!entityType || !entityId) return null;
  switch (entityType) {
    // Land on the item itself, not just the list it lives in.
    case 'finding': return `/vulnerabilities?finding=${entityId}`;
    case 'engagement': return `/engagements/${entityId}`;
    case 'scan_job': return '/auto-scan';
    case 'report': return `/engagements/${entityId}`;
    default: return null;
  }
}

export default function NotificationBell() {
  const { apiFetch } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await apiFetch('/notifications/unread-count');
      if (res.ok) {
        const data = await res.json();
        setUnreadCount(data.count);
      }
    } catch {}
  }, [apiFetch]);

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      // Unread only: the list is what still needs attention, so anything read
      // drops out of it rather than accumulating.
      const res = await apiFetch('/notifications?limit=10&is_read=false');
      if (res.ok) {
        const data = await res.json();
        setNotifications(data.data);
      }
    } catch {}
    setLoading(false);
  }, [apiFetch]);

  // Poll unread count every 30 seconds
  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Fetch notifications when dropdown opens
  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  const handleMarkAllRead = async () => {
    try {
      await apiFetch('/notifications/read-all', { method: 'POST' });
      setUnreadCount(0);
      setNotifications([]);
    } catch {}
  };

  const handleNotificationClick = async (notification) => {
    // Mark as read
    if (!notification.is_read) {
      try {
        await apiFetch(`/notifications/${notification.id}/read`, { method: 'PATCH' });
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.filter(n => n.id !== notification.id));
      } catch {}
    }
    // Navigate to entity
    const path = getEntityPath(notification.entity_type, notification.entity_id);
    if (path) {
      setOpen(false);
      navigate(path);
    }
  };

  const styles = {
    container: { position: 'relative' },
    bellButton: {
      background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: 6,
      padding: '8px 12px', cursor: 'pointer', position: 'relative', display: 'flex',
      alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s',
      color: 'var(--text)', fontSize: 18
    },
    badge: {
      position: 'absolute', top: -4, right: -4, background: '#ef4444', color: 'white',
      borderRadius: '50%', width: 18, height: 18, fontSize: 10, fontWeight: 700,
      display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1
    },
    dropdown: {
      position: 'absolute', top: 'calc(100% + 8px)', right: 0, width: 380,
      background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 12,
      boxShadow: '0 20px 60px rgba(0,0,0,0.4)', zIndex: 1000, overflow: 'hidden'
    },
    header: {
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '14px 16px', borderBottom: '1px solid var(--border)'
    },
    headerTitle: { fontSize: 15, fontWeight: 600, color: 'var(--text)' },
    markAllBtn: {
      background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer',
      fontSize: 12, fontWeight: 500, padding: 0
    },
    list: { maxHeight: 400, overflowY: 'auto' },
    item: (isRead) => ({
      display: 'flex', gap: 12, padding: '12px 16px', cursor: 'pointer',
      background: isRead ? 'transparent' : 'rgba(59,130,246,0.05)',
      borderBottom: '1px solid var(--border)', transition: 'background 0.15s',
      alignItems: 'flex-start'
    }),
    dot: (color) => ({
      width: 8, height: 8, borderRadius: '50%', background: color,
      flexShrink: 0, marginTop: 6
    }),
    itemContent: { flex: 1, minWidth: 0 },
    itemTitle: (isRead) => ({
      fontSize: 13, fontWeight: isRead ? 400 : 600, color: 'var(--text)',
      marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
    }),
    itemBody: {
      fontSize: 12, color: 'var(--text-secondary)', overflow: 'hidden',
      textOverflow: 'ellipsis', whiteSpace: 'nowrap'
    },
    itemTime: { fontSize: 11, color: 'var(--text-secondary)', marginTop: 2 },
    footer: {
      padding: '10px 16px', textAlign: 'center', borderTop: '1px solid var(--border)'
    },
    viewAllLink: {
      background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer',
      fontSize: 13, fontWeight: 500, padding: 0
    },
    empty: {
      padding: '32px 16px', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13
    }
  };

  return (
    <div style={styles.container} ref={dropdownRef}>
      <button
        style={styles.bellButton}
        onClick={() => setOpen(!open)}
        title="Notifications"
      >
        <span role="img" aria-label="notifications">&#x1F514;</span>
        {unreadCount > 0 && (
          <span style={styles.badge}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={styles.dropdown}>
          <div style={styles.header}>
            <span style={styles.headerTitle}>Notifications</span>
            {unreadCount > 0 && (
              <button style={styles.markAllBtn} onClick={handleMarkAllRead}>
                Mark all as read
              </button>
            )}
          </div>

          <div style={styles.list}>
            {loading && notifications.length === 0 ? (
              <div style={styles.empty}>Loading...</div>
            ) : notifications.length === 0 ? (
              <div style={styles.empty}>You're all caught up</div>
            ) : (
              notifications.map(n => (
                <div
                  key={n.id}
                  style={styles.item(n.is_read)}
                  onClick={() => handleNotificationClick(n)}
                  onMouseEnter={e => e.currentTarget.style.background = 'rgba(59,130,246,0.1)'}
                  onMouseLeave={e => e.currentTarget.style.background = n.is_read ? 'transparent' : 'rgba(59,130,246,0.05)'}
                >
                  <div style={styles.dot(EVENT_COLORS[n.event_type] || '#6b7280')} />
                  <div style={styles.itemContent}>
                    <div style={styles.itemTitle(n.is_read)}>{n.title}</div>
                    {n.body && <div style={styles.itemBody}>{n.body}</div>}
                    <div style={styles.itemTime}>
                      {EVENT_LABELS[n.event_type] || n.event_type} &middot; {timeAgo(n.created_at)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div style={styles.footer}>
            <button
              style={styles.viewAllLink}
              onClick={() => { setOpen(false); navigate('/settings/notifications'); }}
            >
              Notification Settings
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
